import { readdir, readFile, stat } from "node:fs/promises";
import { join, resolve, sep } from "node:path";
import { parseFrontmatter } from "@vortex-os/core";
import type { WorklogEntry, WorklogFrontmatter } from "./types.js";

// `YYYY-MM-DD-keyword.md` (legacy) or `YYYY-MM-DD_HHMM-keyword.md` (with an
// optional creation-time segment so same-day entries sort chronologically in a
// file browser). The `_` before HHMM (vs the `-` before the keyword) keeps a
// keyword that itself starts with four digits from being misread as a time.
const FILENAME_PATTERN = /^(\d{4}-\d{2}-\d{2})(?:_(\d{4}))?-(.+)\.md$/;
const MONTH_PATTERN = /^\d{2}$/;
const YEAR_PATTERN = /^\d{4}$/;

/**
 * Directory-backed worklog store. Expected layout: `<rootDir>/YYYY/MM/YYYY-MM-DD-keyword.md`.
 *
 * The store treats missing subdirectories as empty, never as errors —
 * a brand-new month with no entries returns an empty list, not a throw.
 */
export class WorklogStore {
  constructor(readonly rootDir: string) {}

  /** All entries across all years and months, sorted by date ascending. */
  async list(): Promise<readonly WorklogEntry[]> {
    const years = await this.listSubdirs(this.rootDir, YEAR_PATTERN);
    const entries: WorklogEntry[] = [];
    for (const year of years) {
      const yearDir = join(this.rootDir, year);
      const months = await this.listSubdirs(yearDir, MONTH_PATTERN);
      for (const month of months) {
        entries.push(...(await this.entriesIn(join(yearDir, month))));
      }
    }
    return entries.sort(compareWorklog);
  }

  /** Entries within one calendar month. */
  async listByMonth(year: number, month: number): Promise<readonly WorklogEntry[]> {
    const monthDir = join(
      this.rootDir,
      String(year).padStart(4, "0"),
      String(month).padStart(2, "0"),
    );
    const entries = await this.entriesIn(monthDir);
    return entries.sort(compareWorklog);
  }

  /**
   * The first entry matching `date` (`YYYY-MM-DD`). If multiple files exist
   * for that date with different keywords, the lexicographically-first one
   * is returned. Use `list()` when all are needed.
   */
  async get(date: string): Promise<WorklogEntry | undefined> {
    const [year, month] = date.split("-");
    if (!year || !month) return undefined;
    const monthDir = join(this.rootDir, year, month);
    const entries = (await this.entriesIn(monthDir))
      .filter((e) => e.date === date)
      .sort(compareWorklog);
    return entries[0];
  }

  /** Most recent entry by date (descending), then keyword (descending). */
  async getLatest(): Promise<WorklogEntry | undefined> {
    const all = await this.list();
    if (all.length === 0) return undefined;
    return all[all.length - 1];
  }

  /** Resolve the file path for a given (date, keyword), without creating it. */
  pathFor(date: string, keyword: string, time?: string): string {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      throw new Error(`Invalid date: ${date} (expected YYYY-MM-DD)`);
    }
    if (time !== undefined && !/^\d{4}$/.test(time)) {
      throw new Error(`Invalid time: ${time} (expected HHMM)`);
    }
    const [year, month] = date.split("-");
    validateSegment("keyword", keyword);
    const stem = time ? `${date}_${time}-${keyword}` : `${date}-${keyword}`;
    const abs = join(this.rootDir, year!, month!, `${stem}.md`);
    assertContained(abs, this.rootDir);
    return abs;
  }

  private async listSubdirs(dir: string, pattern: RegExp): Promise<readonly string[]> {
    try {
      const entries = await readdir(dir, { withFileTypes: true });
      return entries
        .filter((e) => e.isDirectory() && pattern.test(e.name))
        .map((e) => e.name)
        .sort();
    } catch (e) {
      if ((e as NodeJS.ErrnoException).code === "ENOENT") return [];
      throw e;
    }
  }

  private async entriesIn(monthDir: string): Promise<WorklogEntry[]> {
    let names: string[];
    try {
      names = await readdir(monthDir);
    } catch (e) {
      if ((e as NodeJS.ErrnoException).code === "ENOENT") return [];
      throw e;
    }
    const out: WorklogEntry[] = [];
    for (const name of names) {
      const match = name.match(FILENAME_PATTERN);
      if (!match) continue;
      const path = join(monthDir, name);
      try {
        const info = await stat(path);
        if (!info.isFile()) continue;
      } catch {
        continue;
      }
      const raw = await readFile(path, "utf8");
      const { frontmatter, body } = parseFrontmatter<WorklogFrontmatter>(raw);
      const date = match[1] ?? "";
      const time = match[2]; // optional HHMM segment, undefined for legacy names
      const keyword = match[3] ?? "";
      out.push({ date, ...(time ? { time } : {}), keyword, path, frontmatter, body });
    }
    return out;
  }
}

/**
 * Sort worklog entries chronologically: by date, then by the optional HHMM time
 * segment (entries without a time sort first, preserving legacy ordering), then
 * by keyword for a stable tiebreak.
 */
function compareWorklog(a: WorklogEntry, b: WorklogEntry): number {
  if (a.date !== b.date) return a.date.localeCompare(b.date);
  const ta = a.time ?? "";
  const tb = b.time ?? "";
  if (ta !== tb) return ta.localeCompare(tb);
  return a.keyword.localeCompare(b.keyword);
}

/**
 * Reject a filename segment (keyword) that could escape the store directory or
 * smuggle a path into the filename. `keyword` is operator/model supplied, so
 * the same defensive rules as `memory-extended/src/mcp/document-tools.ts`
 * apply: no path separators, no `..` traversal, no absolute paths / drive
 * letters, no NUL / control chars.
 */
function validateSegment(label: string, value: string): void {
  const v = (value ?? "").trim();
  if (v.length === 0) throw new Error(`${label} is required`);
  if (v.includes("/") || v.includes("\\"))
    throw new Error(`${label} must not contain path separators: ${value}`);
  if (/(^|[\\/])\.\.([\\/]|$)/.test(v) || v === "..")
    throw new Error(`${label} must not contain '..': ${value}`);
  if (v.startsWith("/") || v.startsWith("\\") || /^[a-zA-Z]:/.test(v))
    throw new Error(`${label} must be a relative name, not an absolute path: ${value}`);
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(v))
    throw new Error(`${label} must not contain NUL or control characters: ${value}`);
}

/**
 * Defense-in-depth containment: the resolved absolute target must live inside
 * `rootDir`. Catches any traversal the segment check missed (a malformed
 * year/month, symlinks, future callers). Throws on escape.
 */
function assertContained(abs: string, rootDir: string): void {
  const root = resolve(rootDir);
  const target = resolve(abs);
  if (target !== root && !(target + sep).startsWith(root + sep)) {
    throw new Error(`Refusing to write outside the store directory: ${abs}`);
  }
}
